import React from "react";

interface JasScoreDisplayProps {
  scores: Record<string, any>;
  interpretations: Record<string, any>;
}

const JasScoreDisplay: React.FC<JasScoreDisplayProps> = ({ 
  scores, 
  interpretations 
}) => {
  // Helper function to get color based on JAS scores
  const getJasScoreColor = (score: number, maxScore: number): string => {
    // Calculate percentage of maximum possible score
    const percentage = (score / maxScore) * 100;
    
    if (percentage < 60) return "text-green-500"; // Low Type A behavior
    if (percentage < 80) return "text-yellow-500"; // Moderate Type A behavior
    return "text-red-500"; // High Type A behavior
  };

  // Helper function to get background color based on JAS scores
  const getJasBarColor = (score: number, maxScore: number): string => {
    // Calculate percentage of maximum possible score
    const percentage = (score / maxScore) * 100;
    
    if (percentage < 60) return "bg-green-500"; // Low Type A behavior
    if (percentage < 80) return "bg-yellow-500"; // Moderate Type A behavior
    return "bg-red-500"; // High Type A behavior
  };

  // Helper function to determine bar width as percentage
  const getBarWidth = (score: number, maxScore: number): string => {
    // Calculate percentage of maximum possible score (min 5%, max 100%)
    const percentage = (score / maxScore) * 100;
    return `${Math.max(5, Math.min(100, percentage))}%`;
  };
  
  // Helper function to get interpretation text based on scores
  const getInterpretationText = (key: string, score: number, maxScore: number): string => {
    // If we have an interpretation from the API, use that
    if (interpretations && interpretations[key]) {
      return interpretations[key];
    }
    
    // Otherwise generate a default interpretation based on score
    const percentage = (score / maxScore) * 100;
    
    if (percentage < 60) {
      return "Bajo nivel";
    } else if (percentage < 80) {
      return "Nivel moderado";
    } else {
      return "Alto nivel";
    }
  };
  
  // Define the maximum possible scores for each scale
  // These values may need adjustment based on the actual JAS scoring system
  const maxScores = {
    tipo_a: 370, // Maximum possible for global Type A
    prisa_impaciencia: 200, // Estimate for Factor S
    implicacion_trabajo: 200, // Estimate for Factor J
    competitividad: 200 // Estimate for Factor H
  };
  
  // Debug log
  console.log("JAS scores:", scores);
  console.log("JAS interpretations:", interpretations);

  return (
    <div className="space-y-8">
      <div className="mb-6">
        <h5 className="text-lg font-bold text-navy-700 dark:text-white mb-2">
          Escala de Actividad de Jenkins (JAS)
        </h5>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Mide el Patrón de Conducta Tipo A y sus componentes
        </p>

        <div className="space-y-4">
          {Object.entries(scores).map(([key, score]: [string, any]) => {
            const maxScore = maxScores[key as keyof typeof maxScores] || 200;
            const scoreValue = score.raw_score || 0;
            const interpretationText = getInterpretationText(key, scoreValue, maxScore);
            const textColor = getJasScoreColor(scoreValue, maxScore);
            const barColor = getJasBarColor(scoreValue, maxScore);
            const barWidth = getBarWidth(scoreValue, maxScore);
            
            return (
              <div key={key} className="w-full">
                <div className="flex flex-wrap justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {score.name}
                  </span>
                  <div className="flex items-center">
                    <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">
                      {scoreValue}
                    </span>
                    <span className={`text-sm font-medium ${textColor}`}>
                      {interpretationText}
                    </span>
                  </div>
                </div>
                
                <div className="w-full bg-gray-200 dark:bg-navy-700 rounded-full h-2.5">
                  <div 
                    className={`h-2.5 rounded-full ${barColor}`} 
                    style={{ width: barWidth }}
                  ></div>
                </div>
                
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {score.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700">
        <h5 className="text-md font-bold text-navy-700 dark:text-white mb-3">
          Leyenda
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h6 className="text-sm font-semibold mb-2">Patrón de Conducta Tipo A</h6>
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <span className="text-xs">Bajo nivel (&lt; 60% de la puntuación máxima)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                <span className="text-xs">Nivel moderado (60-80% de la puntuación máxima)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                <span className="text-xs">Alto nivel (&gt; 80% de la puntuación máxima)</span>
              </div>
            </div>
          </div>
          <div>
            <h6 className="text-sm font-semibold mb-2">Implicaciones Clínicas</h6>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Puntuaciones altas en el patrón de conducta Tipo A están asociadas con mayor riesgo cardiovascular.
              El Tipo A se caracteriza por competitividad excesiva, impaciencia, hostilidad y un sentido de urgencia temporal.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JasScoreDisplay; 